import { useEffect, useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import {
  User,
  Shield,
  Save,
  Loader2,
  X,
  Mail,
  Lock,
  UserCheck,
  AlertTriangle,
  Globe,
  Image as ImageIcon,
} from "lucide-react";
import { SearchableSelect, InputCustom, MultiSelect } from "../../components";
import usersService from "../../services/usersService";
import rolesService from "../../services/rolesService";
import clientsService from "../../services/clientsService";
import toast from "react-hot-toast";

/* -------------------------------------------------------
   Helpers
------------------------------------------------------- */
const toMimeAndBase64 = (dataUrl) => {
  if (!dataUrl || typeof dataUrl !== "string" || !dataUrl.startsWith("data:")) {
    return { mime: null, base64: null };
  }
  const [head, body] = dataUrl.split(",");
  const match = head.match(/^data:(.*);base64$/);
  return { mime: match?.[1] || null, base64: body || null };
};

const MAX_IMG_BYTES = 3 * 1024 * 1024; // 3MB

/* -------------------------------------------------------
   Component
------------------------------------------------------- */
const UserFormModal = ({ isOpen, onClose, userId = null, onSuccess }) => {
  const { t } = useTranslation();

  // Remote options
  const [roles, setRoles] = useState([]);
  const [clients, setClients] = useState([]);

  // UI states
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Existing avatar (edit mode preview)
  const [existingAvatarUrl, setExistingAvatarUrl] = useState("");

  // Static options
  const statusOptions = [
    { id: 1, name: t("Active"), value: "active" },
    { id: 2, name: t("Inactive"), value: "inactive" },
    { id: 3, name: t("Suspended"), value: "suspended" },
  ];

  // Form data
  const [formData, setFormData] = useState({
    email: "",
    fullName: "",
    password: "",
    etat: "active",
    role: "",
    doubleAuth: false,
    clients: [],
    profileImageBase64: "", // dataURL string
  });

  // Errors
  const [errors, setErrors] = useState({
    email: "",
    fullName: "",
    password: "",
    role: "",
    profileImageBase64: "",
  });

  /* -------------------------------------------------------
     Effects
  ------------------------------------------------------- */
  useEffect(() => {
    if (!isOpen) return;

    const fetchAll = async () => {
      try {
        setLoading(true);
        await Promise.all([getRoles(), getClients()]);
        if (userId) {
          await getUserToUpdate();
        } else {
          resetForm();
        }
      } catch (e) {
        console.error("Init error:", e);
        toast.error(t("Failed to load form data"));
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId]);

  /* -------------------------------------------------------
     Loaders
  ------------------------------------------------------- */
  const getRoles = async () => {
    try {
      const res = await rolesService.search({ withClient: false });
      if (res?.data?.statusCode === 200) {
        const formatted = (res.data.data || []).map((role) => ({
          id: role._id,
          name: t(role.name),
          value: role._id,
          canonicalName: role.name, // keep original for logic checks
        }));
        setRoles(formatted);
      }
    } catch (error) {
      console.error("Role fetch failed:", error?.response?.data || error);
      toast.error(t("Failed to load roles"));
    }
  };

  const getClients = async () => {
    try {
      const res = await clientsService.searchClients();
      if (res?.data?.statusCode === 200) {
        const formatted = (res.data.data || []).map((c) => ({
          id: c._id,
          name: c.fullName,
          value: c._id,
        }));
        setClients(formatted);
      }
    } catch (error) {
      console.error("Clients fetch failed:", error?.response?.data || error);
      toast.error(t("Failed to load clients"));
    }
  };

  const getUserToUpdate = async () => {
    try {
      const res = await usersService.getUser(userId);
      if (res?.data?.statusCode === 200) {
        const data = res.data.data;

        const formattedClients = (data?.clients || []).map((client) => ({
          id: client._id,
          name: client.fullName,
          value: client._id,
        }));

        setFormData((prev) => ({
          ...prev,
          fullName: data?.user?.fullName || "",
          email: data?.user?.email || "",
          etat: data?.user?.status || "active",
          role: data?.user?.role?._id || "",
          doubleAuth: data?.user?.doubleAuth || false,
          clients: formattedClients,
          profileImageBase64: "", // keep empty for edit; show existing via URL
        }));

        setExistingAvatarUrl(
          data?.user?.avatarUrl || data?.user?.photoUrl || data?.user?.imageUrl || ""
        );
      }
    } catch (error) {
      console.error("User fetch failed:", error?.response?.data || error);
      toast.error(t("Failed to load user data"));
    }
  };

  /* -------------------------------------------------------
     Handlers
  ------------------------------------------------------- */
  const resetForm = () => {
    setFormData({
      email: "",
      fullName: "",
      password: "",
      etat: "active",
      role: "",
      doubleAuth: false,
      clients: [],
      profileImageBase64: "",
    });
    setExistingAvatarUrl("");
    setErrors({
      email: "",
      fullName: "",
      password: "",
      role: "",
      profileImageBase64: "",
    });
  };

  const handleChange = (field, value) => {
    setFormData((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: "" }));
  };

  const handleImageChange = (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setErrors((e) => ({
        ...e,
        profileImageBase64: t("Please select an image file"),
      }));
      return;
    }

    if (file.size > MAX_IMG_BYTES) {
      setErrors((e) => ({
        ...e,
        profileImageBase64: t("Image too large (max 3 MB)"),
      }));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setFormData((f) => ({ ...f, profileImageBase64: reader.result }));
      setExistingAvatarUrl(""); // prefer the newly chosen image
      setErrors((e) => ({ ...e, profileImageBase64: "" }));
    };
    reader.onerror = () => {
      setErrors((e) => ({ ...e, profileImageBase64: t("Failed to read image") }));
    };
    reader.readAsDataURL(file);
  };

  const validateForm = () => {
    const newErrors = {
      email: "",
      fullName: "",
      password: "",
      role: "",
      profileImageBase64: "",
    };

    let isValid = true;

    if (!formData.email) {
      newErrors.email = t("Email is required");
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("Email is invalid");
      isValid = false;
    }

    if (!formData.fullName) {
      newErrors.fullName = t("Full name is required");
      isValid = false;
    }

    if (!userId && !formData.password) {
      newErrors.password = t("Password is required for new users");
      isValid = false;
    }

    if (!formData.role) {
      newErrors.role = t("Role is required");
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setProcessing(true);

      // Build payload
      const payload = {
        email: formData.email,
        fullName: formData.fullName,
        etat: formData.etat,
        role: formData.role,
        doubleAuth: formData.doubleAuth,

      };

      if (!userId && formData.password) {
        payload.password = formData.password;
      }

      // Attach avatar data
      if (formData.profileImageBase64) {
        const { mime, base64 } = toMimeAndBase64(formData.profileImageBase64);
        if (mime && base64) {
          payload.avatar = { mime, base64 }; // backend expects { mime, base64 }
        }
      } else if (!formData.profileImageBase64 && !existingAvatarUrl) {
        // optional: explicitly remove avatar if user clicked "Remove"
        payload.avatar = null;
      }

      let response = null;
      if (userId) {
        response = await usersService.updateUser(userId, payload);
      } else {
        response = await usersService.createUser(payload);
      }

      const responseData = response?.data;
      if (responseData?.statusCode === 200) {
        toast.success(responseData?.message || t("Saved successfully"));
        onSuccess && onSuccess();
        onClose();
      } else {
        toast.error(responseData?.message || t("An error occurred"));
      }
    } catch (error) {
      console.error("User operation failed:", error?.response?.data || error);
      toast.error(error?.response?.data?.message || t("An error occurred"));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-xl bg-white dark:bg-neutral-950 shadow-xl transition-all">
                {/* Header */}
                <div className="border-b border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-5">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-lg font-medium text-neutral-900 dark:text-white flex items-center gap-2">
                      <User className="h-5 w-5 text-neutral-500 dark:text-neutral-400" />
                      {userId ? t("Update User") : t("Create New User")}
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="p-1.5 rounded-full text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800 transition-colors"
                      type="button"
                      aria-label="Close"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 size={30} className="animate-spin text-neutral-600 dark:text-neutral-400" />
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Full Name */}
                        <div className="space-y-1">
                          <InputCustom
                            label={t("Full Name")}
                            placeholder={t("Enter full name")}
                            value={formData.fullName}
                            onChange={(v) => handleChange("fullName", v)}
                            icon={<User size={16} className="text-neutral-500 dark:text-neutral-400" />}
                          />
                          {errors.fullName && (
                            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                              <AlertTriangle size={12} />
                              {errors.fullName}
                            </p>
                          )}
                        </div>

                        {/* Email */}
                        <div className="space-y-1">
                          <InputCustom
                            type="email"
                            label={t("Email Address")}
                            placeholder={t("Enter email address")}
                            value={formData.email}
                            onChange={(v) => handleChange("email", v)}
                            icon={<Mail size={16} className="text-neutral-500 dark:text-neutral-400" />}
                          />
                          {errors.email && (
                            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                              <AlertTriangle size={12} />
                              {errors.email}
                            </p>
                          )}
                        </div>

                        {/* Password (create only) */}
                        {!userId && (
                          <div className="space-y-1">
                            <InputCustom
                              type="password"
                              label={t("Password")}
                              placeholder={t("Enter password")}
                              value={formData.password}
                              onChange={(v) => handleChange("password", v)}
                              icon={<Lock size={16} className="text-neutral-500 dark:text-neutral-400" />}
                            />
                            {errors.password && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertTriangle size={12} />
                                {errors.password}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Role */}
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                            {t("Role")}
                          </label>
                          <SearchableSelect
                            options={roles}
                            value={formData.role}
                            onChange={(v) => handleChange("role", v)}
                            placeholder={t("Select a role")}
                            icon={<Shield size={16} />}
                          />
                          {errors.role && (
                            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                              <AlertTriangle size={12} />
                              {errors.role}
                            </p>
                          )}
                        </div>

                        {/* Status */}
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                            {t("Status")}
                          </label>
                          <SearchableSelect
                            options={statusOptions}
                            value={formData.etat}
                            onChange={(v) => handleChange("etat", v)}
                            placeholder={t("Select status")}
                            icon={<UserCheck size={16} />}
                          />
                        </div>

                        {/* Profile Image */}
                        <div className="space-y-1 md:col-span-2">
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                            {t("Profile Image")}
                          </label>

                          <div className="flex items-center gap-4">
                            <div className="w-20 h-20 rounded-full overflow-hidden bg-neutral-100 border border-neutral-200 dark:border-neutral-800 dark:bg-neutral-900/30 flex items-center justify-center">
                              {formData.profileImageBase64 ? (
                                <img
                                  src={formData.profileImageBase64}
                                  alt="preview"
                                  className="w-full h-full object-cover"
                                />
                              ) : existingAvatarUrl ? (
                                <img
                                  src={existingAvatarUrl}
                                  alt="current"
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center text-[10px] text-neutral-400">
                                  <ImageIcon size={14} />
                                  {t("No image")}
                                </div>
                              )}
                            </div>

                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <label className="inline-flex items-center px-3 py-2 text-sm font-medium bg-white border border-neutral-300 rounded-xl hover:bg-neutral-50 dark:bg-transparent dark:text-neutral-300 dark:border-neutral-600 dark:hover:bg-neutral-800 cursor-pointer">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => handleImageChange(e.target.files?.[0])}
                                  />
                                  {t("Upload")}
                                </label>

                                {(formData.profileImageBase64 || existingAvatarUrl) && (
                                  <button
                                    type="button"
                                    className="px-3 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-xl hover:bg-neutral-50 dark:bg-transparent dark:text-neutral-300 dark:border-neutral-600 dark:hover:bg-neutral-800"
                                    onClick={() => {
                                      setFormData((f) => ({ ...f, profileImageBase64: "" }));
                                      setExistingAvatarUrl("");
                                      setErrors((e) => ({ ...e, profileImageBase64: "" }));
                                    }}
                                  >
                                    {t("Remove")}
                                  </button>
                                )}
                              </div>
                              <p className="text-xs text-neutral-500 mt-1">{t("PNG/JPG up to 3 MB")}</p>
                              {errors.profileImageBase64 && (
                                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                  <AlertTriangle size={12} />
                                  {errors.profileImageBase64}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 2FA Toggle */}
                      {/* <div className="flex items-center space-x-3 p-3 bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/50 rounded-xl border border-neutral-200 dark:border-neutral-800/50">
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-neutral-900 dark:text-white flex items-center gap-2">
                            <Shield size={16} className="text-neutral-500 dark:text-neutral-400" />
                            {t("Two-Factor Authentication")}
                          </h3>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                            {t("Enable email verification during login")}
                          </p>
                        </div>
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.doubleAuth}
                            onChange={(e) => handleChange("doubleAuth", e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="relative w-11 h-6 bg-neutral-200 rounded-full peer dark:bg-neutral-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-neutral-600 peer-checked:bg-blue-600" />
                        </label>
                      </div> */}

                      {/* Info box */}
                      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/30">
                        <div className="flex items-center gap-2 mb-1">
                          <Shield size={16} className="text-blue-600 dark:text-blue-400" />
                          <h3 className="text-sm font-medium text-blue-700 dark:text-blue-400">
                            {t("User Access Information")}
                          </h3>
                        </div>
                        <p className="text-xs text-blue-600 dark:text-blue-300">
                          {userId
                            ? t(
                              "Updating a user's role will change their access permissions. Password will only be updated if a new one is provided."
                            )
                            : t(
                              "New users will receive an email with their login credentials. Make sure the email address is correct."
                            )}
                        </p>
                      </div>
                    </form>
                  )}
                </div>

                {/* Footer */}
                <div className="border-t border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-5 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-xl hover:bg-neutral-50 dark:bg-transparent dark:text-neutral-300 dark:border-neutral-600 dark:hover:bg-neutral-800 focus:outline-none"
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={processing || loading}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t("Processing...")}
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        {userId ? t("Update User") : t("Create User")}
                      </>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default UserFormModal;
